import React, { useState, useEffect } from 'react';
import { fetchTrending } from '../../hooks/useMarketIntelligence';

const SENTIMENT_CHIP = {
  bullish: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
  bearish: 'bg-rose-500/20 border-rose-500/30 text-rose-400',
  neutral: 'bg-slate-500/20 border-slate-500/30 text-slate-400',
};

function sentimentKey(score) {
  if (score > 0.1) return 'bullish';
  if (score < -0.1) return 'bearish';
  return 'neutral';
}

export default function TrendingTokensCard({ config }) {
  const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrending(apiBaseUrl, { time_window: '24h', limit: 10 })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [apiBaseUrl]);

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
        Trending Tokens
        <span className="ml-auto text-xs text-slate-500 font-normal">24h</span>
      </h4>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <svg className="animate-spin h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      )}

      {error && (
        <p className="text-xs text-slate-500 py-4 text-center">Trending data unavailable</p>
      )}

      {data && (
        <div className="divide-y divide-slate-700/40">
          {data.trending?.map((token, idx) => {
            const sk = sentimentKey(token.avg_sentiment);
            const tvSymbol = `${token.token_symbol}USD`;
            const chartUrl = `https://www.tradingview.com/symbols/${tvSymbol}/`;
            return (
              <div key={token.token_symbol} className="flex items-center gap-2 py-1.5 min-w-0">
                <span className="w-4 text-sm font-bold text-cyan-400 shrink-0 text-right">{idx + 1}</span>
                <span className="w-10 text-sm font-bold text-white shrink-0">{token.token_symbol}</span>
                <span className={`shrink-0 hidden sm:inline-block px-1.5 py-0.5 border text-xs rounded ${SENTIMENT_CHIP[sk]}`}>{sk}</span>
                <div className="flex-1 min-w-0 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Math.round((token.avg_impact || 0) * 100)}%` }} />
                </div>
                <a
                  href={chartUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 bg-cyan-700/30 hover:bg-cyan-700/50 text-slate-300 text-xs font-medium py-0.5 px-2 rounded transition-colors"
                >
                  Chart
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
